import { api } from "@/lib/apiClient";

export type CompanyLedgerMode = "account_ledger" | "daily_vouchers";

export type CompanyLedgerLine = {
  id: number;
  date: string | null;
  voucher: string | null;
  voucher_type: string | null;
  account_id: number | null;
  account_code: string | null;
  account_name: string | null;
  party_id: number | null;
  party_name: string | null;
  product_id: number | null;
  product_name: string | null;
  particulars: string[];
  from_to: string | null;
  debit: number;
  credit: number;
  balance: number;
  balance_side: "Dr" | "Cr";
};

export type CompanyLedger = {
  mode: CompanyLedgerMode;
  account: {
    id: number;
    code: string;
    name: string;
  } | null;
  filters: {
    account_id: number | null;
    date_from: string | null;
    date_to: string | null;
  };
  lines: CompanyLedgerLine[];
  totals: {
    opening_balance: number;
    opening_balance_side: "Dr" | "Cr";
    debit: number;
    credit: number;
    balance: number;
    balance_side: "Dr" | "Cr";
  };
};

type ApiCompanyLedger = {
  mode?: string;
  account?: { id?: number; code?: string; name?: string } | null;
  filters?: {
    account_id?: number | null;
    date_from?: string | null;
    date_to?: string | null;
  };
  lines?: Array<Record<string, unknown>>;
  totals?: {
    opening_balance?: number;
    opening_balance_side?: string;
    debit?: number;
    credit?: number;
    balance?: number;
    balance_side?: string;
  };
};

export type FetchCompanyLedgerParams = {
  accountId?: string | number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
};

function ledgerQueryParams(params: FetchCompanyLedgerParams) {
  const accountId = params.accountId != null && String(params.accountId).trim() !== ""
    ? Number(params.accountId)
    : null;

  return {
    ...(accountId != null && accountId > 0 ? { account_id: accountId } : {}),
    ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
    ...(params.dateTo ? { date_to: params.dateTo } : {}),
  };
}

async function downloadLedgerBlob(url: string, params: FetchCompanyLedgerParams, filename: string): Promise<void> {
  const response = await api.get(url, {
    params: ledgerQueryParams(params),
    responseType: "blob",
  });
  const blob = new Blob([response.data], {
    type: response.headers["content-type"] || "application/octet-stream",
  });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

function exportStamp(): string {
  return new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
}

export async function downloadCompanyLedgerPdf(params: FetchCompanyLedgerParams): Promise<void> {
  const isDaily = !(params.accountId != null && String(params.accountId).trim() !== "");
  const prefix = isDaily ? "daily-vouchers" : "general-ledger";
  await downloadLedgerBlob("/api/v1/accounts/company-ledger/pdf", params, `${prefix}-${exportStamp()}.pdf`);
}

export async function downloadCompanyLedgerExcel(params: FetchCompanyLedgerParams): Promise<void> {
  const isDaily = !(params.accountId != null && String(params.accountId).trim() !== "");
  const prefix = isDaily ? "daily-vouchers" : "general-ledger";
  await downloadLedgerBlob("/api/v1/accounts/company-ledger/excel", params, `${prefix}-${exportStamp()}.csv`);
}

function asSide(value: unknown): "Dr" | "Cr" {
  return String(value ?? "").toLowerCase() === "cr" ? "Cr" : "Dr";
}

function asMode(value: unknown): CompanyLedgerMode {
  return String(value ?? "") === "daily_vouchers" ? "daily_vouchers" : "account_ledger";
}

function mapLine(row: Record<string, unknown>): CompanyLedgerLine {
  const particularsRaw = row.particulars;
  const particulars = Array.isArray(particularsRaw)
    ? particularsRaw.map((p) => String(p)).filter(Boolean)
    : typeof particularsRaw === "string" && particularsRaw.trim()
      ? [particularsRaw.trim()]
      : [];

  return {
    id: Number(row.id ?? 0),
    date: (row.date as string | null) ?? null,
    voucher: (row.voucher as string | null) ?? null,
    voucher_type: (row.voucher_type as string | null) ?? null,
    account_id: row.account_id != null ? Number(row.account_id) : null,
    account_code: (row.account_code as string | null) ?? null,
    account_name: (row.account_name as string | null) ?? null,
    party_id: row.party_id != null ? Number(row.party_id) : null,
    party_name: (row.party_name as string | null) ?? null,
    product_id: row.product_id != null ? Number(row.product_id) : null,
    product_name: (row.product_name as string | null) ?? null,
    particulars,
    from_to: (row.from_to as string | null) ?? null,
    debit: Number(row.debit ?? 0),
    credit: Number(row.credit ?? 0),
    balance: Number(row.balance ?? 0),
    balance_side: asSide(row.balance_side),
  };
}

export async function fetchCompanyLedger(params: FetchCompanyLedgerParams): Promise<CompanyLedger> {
  const response = await api.get<{ data: ApiCompanyLedger }>("/api/v1/accounts/company-ledger", {
    params: ledgerQueryParams(params),
  });
  const data = response.data?.data ?? {};
  const account = data.account;
  return {
    mode: asMode(data.mode),
    account: account?.id
      ? {
          id: Number(account.id),
          code: account.code ?? "",
          name: account.name ?? "",
        }
      : null,
    filters: {
      account_id: data.filters?.account_id != null ? Number(data.filters.account_id) : null,
      date_from: data.filters?.date_from ?? null,
      date_to: data.filters?.date_to ?? null,
    },
    lines: Array.isArray(data.lines) ? data.lines.map(mapLine) : [],
    totals: {
      opening_balance: Number(data.totals?.opening_balance ?? 0),
      opening_balance_side: asSide(data.totals?.opening_balance_side),
      debit: Number(data.totals?.debit ?? 0),
      credit: Number(data.totals?.credit ?? 0),
      balance: Number(data.totals?.balance ?? 0),
      balance_side: asSide(data.totals?.balance_side),
    },
  };
}

export function formatLedgerAccountLabel(line: Pick<CompanyLedgerLine, "account_code" | "account_name">): string {
  const code = (line.account_code ?? "").trim();
  const name = (line.account_name ?? "").trim();
  if (code && name) return `${code} — ${name}`;
  return name || code || "—";
}
